import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prescribedDrugsStatsAction from './prescribedDrugsStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import expression from '~/prix/types/expression'
import { dateOrIsoString } from '~/prix/types/date'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { attendanceShape } from '../../attendance/stats/attendanceStats.definition'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const catmatShape = z.object({
  code: z.string().describe('Código CATMAT'),
  activeIngredient: z.string().describe('Princípio ativo'),
  concentration: z.string().describe('Concentração'),
})

export const prescribedDrugsShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  attendanceOriginalId: z.string().describe('ID original do atendimento'),
  catmatCode: z.string().describe('Código CATMAT do medicamento'),
  doseFrequencyType: enumerated({
    notSpecified: 'Não especificado',
    shift: 'Turno',
    frequency: 'Frequência',
    interval: 'Intervalo',
    fractionated: 'Fracionado',
  }).describe('Tipo da frequência da dose'),
  doseFrequency: z.string().describe('Frequência da dose'),
  dose: z.string().describe('Dose do medicamento'),
  doseDescription: z.string().describe('Descrição da dose'),
  treatmentDuration: z.number().describe('Duração do tratamento em dias'),
  administrationRoute: enumerated({
    notSpecified: 'Não especificado',
    oral: 'Oral',
    parenteral: 'Parenteral',
    local: 'Local',
    others: 'Outros',
    buccal: 'Bucal',
    capillary: 'Capilar',
    dermatological: 'Dermatológico',
    epidural: 'Epidural',
    inhalation: 'Inalação',
    nasalInhalation: 'Inalação nasal',
    oralInhalation: 'Inalação oral',
    intraarterial: 'Intraarterial',
    intraarticular: 'Intra-articular',
    intradermal: 'Intradérmica',
    intramuscular: 'Intramuscular',
    intrathecal: 'Intratecal',
    intrauterine: 'Intrauterino',
    intravenous: 'Intravenoso',
    irrigation: 'Irrigação',
    nasal: 'Nasal',
    ophthalmic: 'Oftálmico',
    otological: 'Otológico',
    rectal: 'Retal',
    subcutaneous: 'Subcutâneo',
    sublingual: 'Sublingual',
    transdermal: 'Transdérmico',
    urethral: 'Uretral',
    vaginal: 'Vaginal',
  }).describe('Via de administração'),
  isSingleDose: z.boolean().describe('Dose única'),
  isContinuousDose: z.boolean().describe('Uso contínuo'),
  prescribedQuantity: z.number().describe('Quantidade prescrita'),
  treatmentStartDate: dateOrIsoString().describe('Data de início do tratamento'),
  ...catmatShape.shape,
  ...attendanceShape.omit({
    originalId: true,
    dataSourceId: true,
  }).shape
})
const prescribedDrugsExpression = expression(prescribedDrugsShape.shape)

export const prescribedDrugsVariable: Variable = {
  key: 'prescribedDrugs',
  categoryKey: 'healthPrescribedDrugs',
  actionDefinitionKey: prescribedDrugsStatsAction.key,
  title: 'Medicamentos prescritos',
  unitSingular: 'medicamento',
  unitPlural: 'medicamentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(prescribedDrugsShape),
  filterableBy: getZodSchemaInfo(
    prescribedDrugsShape.pick({
      /* CIDADÃO */
      'citizen.age': true,
      'citizen.sex': true,
      'citizen.deceased': true,
      /* SAÚDE */
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.homeless': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
      'citizenHealth.nationality': true,
      /* ATENDIMENTO */
      'typeOfAttendance': true,
      'isVaccinationUpToDate': true,
      'bmi': true,
    }),
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.prescribedDrugs,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    /* PRESCRIÇÃO */
    'activeIngredient',
    'concentration',
    'doseFrequency',
    'doseFrequencyType',
    'doseDescription',
    'treatmentDuration',
    'administrationRoute',
    'isSingleDose',
    'isContinuousDose',
    'prescribedQuantity',
    'treatmentStartDate',

    /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.originalId',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',

    /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'teamName',
    'weight',
    'height',
    'typeOfAttendance',
    'isVaccinationUpToDate',
    'lastMenstruationDate',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'trend',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 0,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(prescribedDrugsExpression),
}

const prescribedDrugsStatsDefinition = defineAction({
  ...prescribedDrugsStatsAction,
  input: z.object({
    variable,
    highlight: prescribedDrugsExpression.optional(),
    filter: prescribedDrugsExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(prescribedDrugsShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default prescribedDrugsStatsDefinition
